import { useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDownIcon, ChevronRightIcon, InformationCircleIcon } from '@heroicons/react/16/solid'
import { useDispatch } from '../../../Flux/index.ts'
import { orderWizardActions as wizard } from '../../../Flux/Slice/OrderWizard/OrderWizardSlice.ts'
import type { DraftState } from '../../../Model/OrderWizard/OrderWizardDraft.ts'
import type { ColumnSort } from '../HomePage/Modals/PoolCsvGridView.ts'
import { visibleRowIndices } from '../HomePage/Modals/PoolCsvGridView.ts'
import type { PrimerGridColumn, PrimerGridRow } from '../../../Services/OrderWizard/primerGrid.ts'
import { estimatedRows, isPrimer, isPrimerSide, normalizePrimer, primerGridRows, sortableRows } from '../../../Services/OrderWizard/primerGrid.ts'
import type { PasteTarget } from '../../../Services/OrderWizard/primerPaste.ts'
import { parsePastedText, planPrimerPaste } from '../../../Services/OrderWizard/primerPaste.ts'
import { hasKeyColumn } from '../../../Services/OrderWizard/sheet/sheetMapping.ts'
import { tallyEstimates, validateEstimates } from '../../../Services/OrderWizard/estimateValidation.ts'
import { plural } from '../../../Util/plural.ts'
import { useStableCallback } from '../../../Framework/View/Hooks/useLatest.ts'
import { InfoDisclosure } from './InfoDisclosure.tsx'
import type { GridHover } from './PrimerGrid.tsx'
import { PrimerGrid } from './PrimerGrid.tsx'
import { PrimerPastePanel } from './PrimerPastePanel.tsx'
import { SendSheetCard } from './SendSheetCard.tsx'
import { SheetPicker } from './SheetPicker.tsx'
import { fastqNamesOf } from '../../../Services/OrderWizard/sheetSupport.ts'
import { countItems } from '../../../Services/OrderWizard/sampleLabels.ts'
import { usePasteTelemetry } from './usePasteTelemetry.ts'

/** Where a sheet declares primers: how the estimator would have done. */
const EstimateCheck = ({ draft }: { draft: DraftState }) => {
  const tally = tallyEstimates(validateEstimates(draft))
  if (tally.total === 0) {
    return null
  }
  return (
    <p data-testid="wizard-estimate-check">
      <span className="font-medium">Estimator vs sheet, {plural(tally.total, 'item')}:</span> {tally.correct} correct · {tally.gene_spelling}{' '}
      partial (tag right, gene part spelled differently) · {tally.tag_mismatch} wrong tag · {tally.sheet_untagged} sheet without tag ·{' '}
      {tally.no_estimate} no estimate
    </p>
  )
}

const SAMPLE_SOURCE_TEXT: Record<NonNullable<PrimerGridRow['sampleSource']>, string> = {
  sheet: 'Name from the sample sheet',
  file: 'Name from the file name: no sample sheet row names this sample'
}

const hoverLines = ({ row, column }: GridHover): string[] => {
  if (isPrimerSide(column)) {
    return row[column].details
  }
  switch (column) {
    case 'confidence':
      return row.basis
    case 'source':
      return [row.source, ...row.basis]
    case 'sample':
      return [
        row.sample,
        ...(row.sampleSource ? [SAMPLE_SOURCE_TEXT[row.sampleSource]] : []),
        row.itemType === 'NEGATIVE_CONTROL' ? 'Negative control — change the type on Samples & controls' : 'Sample'
      ]
    case 'file':
      return row[column] ? [row[column]] : []
    default:
      return []
  }
}

const HoverNote = ({ hover }: { hover: GridHover }) => {
  const lines = hoverLines(hover)
  if (lines.length === 0) {
    return null
  }
  return (
    <div
      role="tooltip"
      className="pointer-events-none fixed z-50 max-w-sm rounded border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 shadow"
      style={{ left: hover.bounds.x, top: hover.bounds.y + hover.bounds.height + 2 }}
      data-testid="wizard-primer-hover"
    >
      {lines.map((line) => (
        <p key={line}>{line}</p>
      ))}
    </div>
  )
}

type PasteState = { text: string; target: PasteTarget }

const NOTICE_ACTION = 'btn btn-link btn-xs h-auto min-h-0 px-0 text-slate-700'

/** Whenever any primer is the reads' estimate: say so, and how to be sure. Never blocks. */
const EstimatedPrimersNotice = ({ draft, rows, onPaste }: { draft: DraftState; rows: readonly PrimerGridRow[]; onPaste?: () => void }) => {
  const estimated = estimatedRows(rows)
  if (estimated.length === 0) {
    return null
  }
  return (
    <div className="flex items-start gap-2 rounded-lg bg-sky-50 px-3 py-2 text-sm text-slate-700" data-testid="wizard-primers-estimated">
      <InformationCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-sky-600" />
      <div className="space-y-1">
        <p>
          The tags of {countItems(estimated)} were estimated from the reads (experimental), not given by you. Please check them — or, to be
          sure, add a sample sheet with the exact primers.
        </p>
        <p className="flex flex-wrap items-center gap-x-3">
          <SheetPicker draft={draft} testId="wizard-primers-estimated-sheet" className={NOTICE_ACTION}>
            Add a sample sheet
          </SheetPicker>
          {onPaste && (
            <button type="button" className={NOTICE_ACTION} data-testid="wizard-primers-estimated-paste" onClick={onPaste}>
              Paste from a spreadsheet
            </button>
          )}
        </p>
      </div>
    </div>
  )
}

/** A paste whose rows replaced the sample sheet: the previous sheet can come back. */
const ReplacedSheetNote = ({ draft }: { draft: DraftState }) => {
  const dispatch = useDispatch()
  if (!draft.replacedSheet || !draft.sheet) {
    return null
  }
  return (
    <p className="flex flex-wrap items-center gap-x-1.5 text-xs text-slate-600" data-testid="wizard-sheet-replaced">
      The pasted rows replaced the sample sheet {draft.replacedSheet.name}.
      <button
        type="button"
        className="btn btn-link btn-xs px-0"
        data-testid="wizard-sheet-restore"
        onClick={() => dispatch(wizard.sheetRestored({ draftId: draft.id }))}
      >
        Restore previous sheet
      </button>
      <button
        type="button"
        className="btn btn-ghost btn-xs btn-square"
        aria-label="Dismiss"
        onClick={() => dispatch(wizard.replacedSheetDismissed({ draftId: draft.id }))}
      >
        ×
      </button>
    </p>
  )
}

const NO_FLAGS: ReadonlySet<string> = new Set()

/**
 * Per item: the exact primers as one sortable grid, each cell what the item gets. Editing a primer
 * enters it; clearing it goes back to the sheet's or the estimate. A pasted block is previewed before it
 * applies. With nothing to check the grid folds into one line.
 */
export const SamplePrimersGrid = ({
  draft,
  flagged = NO_FLAGS,
  pasteRequests = 0
}: {
  draft: DraftState
  /** Items an issue of this step names. */
  flagged?: ReadonlySet<string>
  /** Bumped by "Paste from a spreadsheet" on a card above the grid. */
  pasteRequests?: number
}) => {
  const dispatch = useDispatch()
  const rows = useMemo(() => primerGridRows(draft, flagged), [draft, flagged])
  const [sort, setSort] = useState<ColumnSort | null>(null)
  const [filter, setFilter] = useState('')
  const view = useMemo(() => visibleRowIndices(sortableRows(rows), filter ? { sample: filter } : {}, sort), [rows, filter, sort])
  const [expanded, setExpanded] = useState(false)
  const [focus, setFocus] = useState<{ row: number; column: PrimerGridColumn }>()
  const [hover, setHover] = useState<GridHover>()
  const [editError, setEditError] = useState<string>()
  const [paste, setPaste] = useState<PasteState>()
  const [selectedRows, setSelectedRows] = useState<PrimerGridRow[]>([])
  const editable = !draft.submission

  const targetAt = (at: { row: number; column: PrimerGridColumn } | undefined): PasteTarget => ({
    view: view.map((index) => rows[index].key),
    row: at?.row ?? 0,
    column: at?.column ?? 'forward'
  })
  const openPaste = () => setPaste((current) => current ?? { text: '', target: targetAt(focus) })

  const openRequested = useStableCallback(() => {
    if (editable) {
      setPaste((current) => current ?? { text: '', target: targetAt(undefined) })
    }
  })
  /* a request made before this grid mounted (on an earlier visit to the step) is not one for it */
  const requestsSeen = useRef(pasteRequests)
  useEffect(() => {
    if (pasteRequests > requestsSeen.current) {
      openRequested()
    }
    requestsSeen.current = pasteRequests
  }, [pasteRequests, openRequested])

  const plan = useMemo(
    () => (paste?.text.trim() ? planPrimerPaste(draft, parsePastedText(paste.text), paste.target) : undefined),
    [draft, paste]
  )
  const reportPaste = usePasteTelemetry(draft.id, plan)

  if (rows.length === 0) {
    return null
  }

  const edit = (row: PrimerGridRow, column: 'forward' | 'reverse', raw: string) => {
    const value = normalizePrimer(raw)
    const cell = row[column]
    if (!value) {
      setEditError(undefined)
      if (cell.origin === 'manual') {
        dispatch(wizard.primersEdited({ draftId: draft.id, edits: [{ key: row.key, [column]: null }] }))
      }
      return
    }
    if (!isPrimer(value)) {
      setEditError(`“${raw.trim()}” is not a primer: 12 or more IUPAC bases (A, C, G, T and the degenerate codes).`)
      return
    }
    setEditError(undefined)
    if (value === cell.value && cell.origin !== 'manual' && !cell.tentative) {
      return
    }
    dispatch(wizard.primersEdited({ draftId: draft.id, edits: [{ key: row.key, [column]: value }] }))
  }

  const apply = () => {
    if (plan?.kind !== 'keyed' && plan?.kind !== 'positional') {
      return
    }
    reportPaste()
    if (plan.edits.length > 0) {
      dispatch(wizard.primersEdited({ draftId: draft.id, edits: plan.edits }))
    }
    if (plan.kind === 'keyed' && plan.sheet) {
      dispatch(wizard.sheetLoaded({ draftId: draft.id, sheet: plan.sheet, fromPaste: true }))
    }
    setPaste(undefined)
  }

  const entered = selectedRows.filter((row) => row.forward.origin === 'manual' || row.reverse.origin === 'manual')
  const clearEntered = () => {
    dispatch(wizard.primersEdited({ draftId: draft.id, edits: entered.map((row) => ({ key: row.key, forward: null, reverse: null })) }))
    setSelectedRows([])
  }

  const attentionRows = rows.filter((row) => row.attention)
  const attention = attentionRows.length
  const open = expanded || attention > 0 || !!paste
  const noPair = draft.discovery && draft.discovery.primers.status !== 'found'
  const sheetHasPrimers = !!(draft.sheet && hasKeyColumn(draft.sheet.mapping) && draft.sheet.mapping.forwardPrimer)
  const okLabel = `Primers look good for ${countItems(rows)}`

  return (
    <section className="space-y-2" data-testid="wizard-primers">
      <InfoDisclosure testId="wizard-primers-how">
        <p>
          Estimated from each item&apos;s first reads: the bases in front of the gene primer (highlighted) are the tag. Estimates are
          reliable for tagged designs but remain suggestions — primers from a sheet or entered here are the source of truth.
          {sheetHasPrimers
            ? ' The sheet’s primers are written into the order.'
            : ' Without primers in a sheet, confident estimates are written into the order, marked as estimated from reads — please check them.'}
        </p>
        <p>
          Click a primer to change it; clear it to go back to the sheet’s or the estimate. Paste a block from a spreadsheet: with a header
          naming the samples it is matched by name, plain primers fill down from the selected cell.
        </p>
        <p>A customer sample name in grey is taken from the file name, because no sample sheet row names that sample.</p>
        <EstimateCheck draft={draft} />
      </InfoDisclosure>
      {editable && <EstimatedPrimersNotice draft={draft} rows={rows} onPaste={paste ? undefined : openPaste} />}
      {noPair && (
        <p className="text-sm text-slate-600" data-testid="wizard-primers-none">
          {draft.discovery?.primers.status === 'partial'
            ? 'The reads show only one primer, so which pair it belongs to — and the exact primers — are not read from them:'
            : 'No registered primer pair was found on the reads, so the exact primers are not read from them:'}{' '}
          they come from a sample sheet, or are entered here.
        </p>
      )}
      {!open ? (
        <button
          type="button"
          className="flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
          aria-expanded={false}
          data-testid="wizard-primers-collapsed"
          onClick={() => setExpanded(true)}
        >
          {okLabel} <ChevronRightIcon className="h-3.5 w-3.5" />
        </button>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            {attention === 0 ? (
              <button
                type="button"
                className="flex items-center gap-1 text-slate-600 hover:text-slate-900"
                aria-expanded
                data-testid="wizard-primers-collapse"
                onClick={() => {
                  setExpanded(false)
                  setPaste(undefined)
                }}
              >
                {okLabel} <ChevronDownIcon className="h-3.5 w-3.5" />
              </button>
            ) : (
              <span className="text-amber-700" data-testid="wizard-primers-attention">
                {countItems(attentionRows)} to check, highlighted
              </span>
            )}
            {rows.length > 8 && (
              <input
                type="search"
                aria-label="Filter by name"
                placeholder="Filter by name"
                className="input input-bordered input-xs w-40"
                value={filter}
                onChange={(event) => setFilter(event.target.value)}
              />
            )}
            {editable && !paste && (
              <button type="button" className="btn btn-ghost btn-xs" data-testid="wizard-primers-paste-open" onClick={openPaste}>
                Paste from a spreadsheet
              </button>
            )}
            {editable && entered.length > 0 && (
              <button type="button" className="btn btn-ghost btn-xs" data-testid="wizard-primers-clear-entered" onClick={clearEntered}>
                Clear entered primers ({entered.length})
              </button>
            )}
          </div>
          <ReplacedSheetNote draft={draft} />
          <PrimerGrid
            rows={rows}
            view={view}
            sort={sort}
            editable={editable}
            onSort={setSort}
            onEdit={edit}
            onPaste={(at, values) => setPaste({ text: values.map((cells) => cells.join('\t')).join('\n'), target: targetAt(at) })}
            onFocusChange={setFocus}
            onRowsSelected={setSelectedRows}
            onHover={setHover}
          />
          {editError && (
            <p className="text-xs text-red-700" data-testid="wizard-primer-edit-error">
              {editError}
            </p>
          )}
          {paste && (
            <PrimerPastePanel
              text={paste.text}
              target={paste.target}
              plan={plan}
              currentSheet={draft.sheet?.name}
              support={
                plan && ((plan.kind === 'unreadable' && plan.sheetLike) || (plan.kind === 'keyed' && plan.lowMatch)) ? (
                  <SendSheetCard
                    orderId={draft.orderId}
                    request={{
                      name: 'Pasted rows',
                      source: { kind: 'text', text: paste.text },
                      columns: { headers: plan.headers },
                      stats:
                        plan.kind === 'keyed'
                          ? { reason: 'low-match', rows: plan.total, matched: plan.matched }
                          : { reason: 'no-key-column', rows: Math.max(parsePastedText(paste.text).length - 1, 0), matched: 0 },
                      fileNames: fastqNamesOf(draft)
                    }}
                  />
                ) : undefined
              }
              onText={(text) => setPaste({ ...paste, text })}
              onApply={apply}
              onCancel={() => {
                reportPaste()
                setPaste(undefined)
              }}
            />
          )}
        </>
      )}
      {hover && open && <HoverNote hover={hover} />}
    </section>
  )
}
